import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import "../css/StudentFeedback.css";

import AgasheVideo from "../StudentFeedback/StudentFeedback.mp4";
import StudentFeedback2Video from "../StudentFeedback/StudentFeedback2.mp4";
import mpsv1 from "../StudentFeedback/mpsvideo1.mp4";
import risv1 from "../StudentFeedback/RIS_FEEDBACK.mp4";

const reviews = [
  {
    id: 1,
    number: "01",
    schoolName: "PanditRao Agashe",
    videoUrl: AgasheVideo,
    accent: "yellow",
  },
  {
    id: 2,
    number: "02",
    schoolName: "Modern Primary School",
    videoUrl: mpsv1,
    accent: "blue",
  },
  {
    id: 3,
    number: "03",
    schoolName: "Rahul International School (RIS)",
    videoUrl: risv1,
    accent: "purple",
  },
  {
    id: 4,
    number: "04",
    schoolName: "Blue Valley English School ",
    videoUrl: StudentFeedback2Video,
    accent: "yellow",
  },
];

const INITIAL_REVIEW_INDEX = 1;

export default function StudentFeedback() {
  const [activeIndex, setActiveIndex] = useState(
    INITIAL_REVIEW_INDEX
  );
  const [isVisible, setIsVisible] = useState(false);

  const sectionRef = useRef(null);
  const filmstripRef = useRef(null);
  const videoRefs = useRef([]);
  const cardRefs = useRef([]);
  const scrollFrameRef = useRef(null);
  const activeIndexRef = useRef(INITIAL_REVIEW_INDEX);

  const getCenteredPosition = useCallback((index) => {
    const filmstrip = filmstripRef.current;
    const card = cardRefs.current[index];

    if (!filmstrip || !card) return 0;

    return (
      card.offsetLeft -
      (filmstrip.clientWidth - card.offsetWidth) / 2
    );
  }, []);

  const scrollToReview = useCallback(
    (index, behavior = "smooth") => {
      const filmstrip = filmstripRef.current;

      if (!filmstrip) return;

      const safeIndex = Math.max(
        0,
        Math.min(reviews.length - 1, index)
      );

      activeIndexRef.current = safeIndex;
      setActiveIndex(safeIndex);

      filmstrip.scrollTo({
        left: getCenteredPosition(safeIndex),
        behavior,
      });
    },
    [getCenteredPosition]
  );

  const syncActiveReview = useCallback(() => {
    const filmstrip = filmstripRef.current;

    if (!filmstrip) return;

    const viewportCenter =
      filmstrip.scrollLeft + filmstrip.clientWidth / 2;

    let closestIndex = activeIndexRef.current;
    let closestDistance = Number.POSITIVE_INFINITY;

    cardRefs.current.forEach((card, index) => {
      if (!card) return;

      const cardCenter =
        card.offsetLeft + card.offsetWidth / 2;

      const distance = Math.abs(
        cardCenter - viewportCenter
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== activeIndexRef.current) {
      activeIndexRef.current = closestIndex;
      setActiveIndex(closestIndex);
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollFrameRef.current) {
      cancelAnimationFrame(scrollFrameRef.current);
    }

    scrollFrameRef.current = requestAnimationFrame(
      syncActiveReview
    );
  }, [syncActiveReview]);

  useLayoutEffect(() => {
    const positionCarousel = () => {
      const filmstrip = filmstripRef.current;

      if (!filmstrip) return;

      filmstrip.scrollTo({
        left: getCenteredPosition(
          activeIndexRef.current
        ),
        behavior: "auto",
      });
    };

    const frame = requestAnimationFrame(
      positionCarousel
    );

    window.addEventListener(
      "resize",
      positionCarousel
    );

    return () => {
      cancelAnimationFrame(frame);

      window.removeEventListener(
        "resize",
        positionCarousel
      );

      if (scrollFrameRef.current) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, [getCenteredPosition]);

  /* ======================================================
     SECTION VISIBILITY
  ====================================================== */

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.25,
      }
    );

    const section = sectionRef.current;

    if (section) {
      observer.observe(section);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  /* ======================================================
     PLAY ONLY THE ACTIVE VIDEO
  ====================================================== */

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;

      if (index === activeIndex && isVisible) {
        video.muted = true;

        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay can be blocked by the browser.
          });
        }
      } else {
        video.pause();
      }
    });
  }, [activeIndex, isVisible]);

  /* ======================================================
     NAVIGATION
  ====================================================== */

  const showPrevious = useCallback(() => {
    if (activeIndexRef.current <= 0) return;

    scrollToReview(
      activeIndexRef.current - 1
    );
  }, [scrollToReview]);

  const showNext = useCallback(() => {
    if (
      activeIndexRef.current >=
      reviews.length - 1
    ) {
      return;
    }

    scrollToReview(
      activeIndexRef.current + 1
    );
  }, [scrollToReview]);

  const selectReview = useCallback(
    (index) => {
      scrollToReview(index);
    },
    [scrollToReview]
  );

  const storyProgress =
    reviews.length > 1
      ? (activeIndex / (reviews.length - 1)) * 100
      : 100;

  return (
    <section
      className="ms-student-section"
      ref={sectionRef}
    >
      {/*
        Keep the helper below the videos intentionally simple.
        No animated shimmer, no looping badge, no carousel teleporting.
      */}
      <style>{`
        .ms-feedback-filmstrip {
          overscroll-behavior-inline: contain;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x mandatory;
        }

        .ms-feedback-card {
          scroll-snap-align: center;
          scroll-snap-stop: normal;
        }

        .ms-feedback-simple-nav {
          width: min(760px, calc(100% - 28px));
          margin: 22px auto 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: #65738c;
        }

        .ms-feedback-simple-button {
          width: 34px;
          height: 34px;
          flex: 0 0 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(27, 42, 69, 0.12);
          border-radius: 50%;
          background: transparent;
          color: #25324a;
          cursor: pointer;
          transition:
            background 160ms ease,
            border-color 160ms ease,
            opacity 160ms ease;
        }

        .ms-feedback-simple-button:hover:not(:disabled) {
          background: rgba(255, 201, 7, 0.08);
          border-color: rgba(255, 201, 7, 0.48);
        }

        .ms-feedback-simple-button:disabled {
          opacity: 0.28;
          cursor: default;
        }

        .ms-feedback-simple-button svg {
          width: 16px;
          height: 16px;
          fill: none;
          stroke: currentColor;
          stroke-width: 2;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .ms-feedback-simple-center {
          min-width: 0;
          width: min(560px, 100%);
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 10px;
        }

        .ms-feedback-simple-copy {
          grid-column: 1 / -1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          color: #67758d;
          font-size: 0.78rem;
          line-height: 1.2;
        }

        .ms-feedback-simple-copy svg {
          width: 19px;
          height: 14px;
          flex: 0 0 auto;
          fill: none;
          stroke: currentColor;
          stroke-width: 1.7;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .ms-feedback-simple-track {
          position: relative;
          height: 3px;
          overflow: hidden;
          border-radius: 999px;
          background: #e7ebf2;
        }

        .ms-feedback-simple-fill {
          position: absolute;
          inset: 0 auto 0 0;
          width: var(--story-progress);
          border-radius: inherit;
          background: linear-gradient(
            90deg,
            #347cf4 0%,
            #ffc907 100%
          );
          transition:
            width 220ms ease-out;
        }

        .ms-feedback-simple-count {
          min-width: 48px;
          color: #8792a5;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-align: right;
          white-space: nowrap;
        }

        .ms-feedback-simple-count strong {
          color: #28354d;
          font-size: 0.8rem;
        }

        @media (max-width: 640px) {
          .ms-feedback-simple-nav {
            width: calc(100% - 16px);
            gap: 8px;
            margin-top: 18px;
          }

          .ms-feedback-simple-button {
            width: 32px;
            height: 32px;
            flex-basis: 32px;
          }

          .ms-feedback-simple-copy {
            font-size: 0.72rem;
          }

          .ms-feedback-simple-center {
            gap: 8px;
          }

          .ms-feedback-simple-count {
            min-width: 43px;
            font-size: 0.68rem;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .ms-feedback-simple-fill {
            transition: none;
          }
        }
      `}</style>

      {/* ==================================================
          BACKGROUND
      ================================================== */}

      <div className="ms-student-grid" />

      <div className="ms-student-glow ms-student-glow-yellow" />
      <div className="ms-student-glow ms-student-glow-blue" />

      <div className="ms-student-circle-decoration">
        <span />
        <span />
      </div>

      <span className="ms-student-spark ms-student-spark-one">
        ✦
      </span>

      <span className="ms-student-spark ms-student-spark-two">
        ✦
      </span>

      <div className="container ms-student-container">
        {/* ==================================================
            HEADING
        ================================================== */}

        <div className="ms-student-heading-row">
          <div className="ms-student-heading">
            <div className="ms-student-kicker">
              <span />
              STUDENT VOICES
            </div>

            <h2>
              Voices of
              <span> our students.</span>
            </h2>
          </div>

          <div className="ms-student-heading-right">
            <p>
              See technology learning through the eyes of
              students experiencing MindSpark in their
              schools.
            </p>

            <div className="ms-feedback-summary">
              <span className="ms-summary-live">
                <i />
                Real experiences
              </span>

              <span className="ms-summary-divider" />

              <strong>
                {reviews.length} featured schools
              </strong>
            </div>
          </div>
        </div>

        {/* ==================================================
            FILMSTRIP
        ================================================== */}

        <div className="ms-feedback-stage">
          <div className="ms-feedback-side-label">
            <span>LEARNING</span>
            <strong>IN ACTION</strong>
          </div>

          <div
            className="ms-feedback-filmstrip"
            ref={filmstripRef}
            onScroll={handleScroll}
            aria-label="Student testimonial stories"
          >
            {reviews.map((item, index) => {
              const isActive =
                index === activeIndex;

              return (
                <article
                  key={item.id}
                  ref={(element) => {
                    cardRefs.current[index] = element;
                  }}
                  className={`ms-feedback-card ${item.accent} ${
                    isActive ? "active" : ""
                  }`}
                  aria-label={`${item.schoolName} student story`}
                  tabIndex={0}
                  onClick={() => {
                    selectReview(index);
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" ||
                      event.key === " "
                    ) {
                      event.preventDefault();
                      selectReview(index);
                    }

                    if (event.key === "ArrowLeft") {
                      event.preventDefault();
                      showPrevious();
                    }

                    if (event.key === "ArrowRight") {
                      event.preventDefault();
                      showNext();
                    }
                  }}
                >
                  <div className="ms-feedback-number">
                    {item.number}
                  </div>

                  <div className="ms-feedback-video-frame">
                    <video
                      ref={(element) => {
                        videoRefs.current[index] =
                          element;
                      }}
                      className="ms-feedback-video"
                      muted
                      playsInline
                      preload="metadata"
                      controls={isActive}
                      onEnded={
                        isActive &&
                        index < reviews.length - 1
                          ? showNext
                          : undefined
                      }
                    >
                      <source
                        src={item.videoUrl}
                        type="video/mp4"
                      />

                      Your browser does not support
                      video.
                    </video>

                    {!isActive && (
                      <div className="ms-feedback-preview-overlay">
                        <div className="ms-preview-play">
                          ▶
                        </div>

                        <span>View story</span>
                      </div>
                    )}

                    {isActive && (
                      <div className="ms-now-watching">
                        <span />
                        NOW WATCHING
                      </div>
                    )}
                  </div>

                  <div className="ms-feedback-card-info">
                    <div>
                      <span className="ms-school-label">
                        SCHOOL
                      </span>

                      <h3>{item.schoolName}</h3>
                    </div>

                    <div className="ms-feedback-card-arrow">
                      {isActive ? "●" : "↗"}
                    </div>
                  </div>

                  {isActive && (
                    <div className="ms-feedback-active-line" />
                  )}
                </article>
              );
            })}
          </div>

          <div className="ms-feedback-side-number">
            <strong>
              {String(activeIndex + 1).padStart(
                2,
                "0"
              )}
            </strong>

            <span />

            {String(reviews.length).padStart(
              2,
              "0"
            )}
          </div>
        </div>

        {/* ==================================================
            SIMPLE SWIPE HELPER
        ================================================== */}

        <div
          className="ms-feedback-simple-nav"
          aria-label="Student story navigation"
        >
          <button
            type="button"
            className="ms-feedback-simple-button"
            onClick={showPrevious}
            disabled={activeIndex === 0}
            aria-label="Previous student story"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14.5 5 7.5 12l7 7" />
            </svg>
          </button>

          <div className="ms-feedback-simple-center">
            <div className="ms-feedback-simple-copy">
              <svg
                viewBox="0 0 30 14"
                aria-hidden="true"
              >
                <path d="M1 7h28M6 2 1 7l5 5M24 2l5 5-5 5" />
              </svg>

              <span>
                Swipe left or right to explore student stories
              </span>
            </div>

            <div
              className="ms-feedback-simple-track"
              aria-hidden="true"
            >
              <span
                className="ms-feedback-simple-fill"
                style={{
                  "--story-progress": `${storyProgress}%`,
                }}
              />
            </div>

            <span
              className="ms-feedback-simple-count"
              aria-live="polite"
            >
              <strong>
                {String(activeIndex + 1).padStart(
                  2,
                  "0"
                )}
              </strong>
              {" / "}
              {String(reviews.length).padStart(
                2,
                "0"
              )}
            </span>
          </div>

          <button
            type="button"
            className="ms-feedback-simple-button"
            onClick={showNext}
            disabled={
              activeIndex === reviews.length - 1
            }
            aria-label="Next student story"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9.5 5 7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* ==================================================
            BOTTOM BRAND LINE
        ================================================== */}

        <div className="ms-feedback-footer">
          <span />

          <p>Learning Through Technology</p>

          <span />
        </div>
      </div>
    </section>
  );
}
